import React, { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

interface PaginationStateLike {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

interface AdminPaginationProps {
  pagination: PaginationStateLike;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const AdminPagination: React.FC<AdminPaginationProps> = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}) => {
  const { page, size, totalPages, totalElements } = pagination;
  const [pageInput, setPageInput] = useState<string | number>(page + 1);

  useEffect(() => {
    setPageInput(page + 1);
  }, [page]);

  const getPageNumbers = () => {
    const pageNumbers: number[] = [];
    const maxVisiblePages = 10;
    let startPage = Math.max(0, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handleDirectPageMove = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setPageInput(raw === '' ? '' : Number(raw));
    const targetPage = parseInt(raw, 10) - 1;
    if (targetPage >= 0 && targetPage < totalPages) {
      onPageChange(targetPage);
    }
  };

  const handlePageInputBlur = () => {
    setPageInput(page + 1);
  };

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onPageSizeChange(parseInt(e.target.value, 10));
  };

  if (totalPages <= 0) {
    return null;
  }

  const pageNumbers = getPageNumbers();

  return (
    <PaginationRoot>
      <PaginationContainer>
        <PageControls>
          <NavButton
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
          >
            <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
              <path
                d="M7 1L1 8L7 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </NavButton>

          <PageNumbers>
            {pageNumbers.map((pageNum) => (
              <PageButton
                key={pageNum}
                type="button"
                $active={pageNum === page}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum + 1}
              </PageButton>
            ))}
          </PageNumbers>

          <NavButton
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
          >
            <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
              <path
                d="M1 1L7 8L1 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </NavButton>
        </PageControls>

        <PageInfo>
          <PageStats>
            <StatsText>
              전체 {totalElements.toLocaleString()}개 중 {((page * size) + 1).toLocaleString()}-
              {Math.min((page + 1) * size, totalElements).toLocaleString()}개
            </StatsText>
          </PageStats>

          <DirectMove>
            <Label>페이지 이동</Label>
            <PageInput
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={handleDirectPageMove}
              onBlur={handlePageInputBlur}
            />
            <TotalPages>/ {totalPages}</TotalPages>
          </DirectMove>

          <PageSizeSelector>
            <Label>페이지당</Label>
            <SizeSelect value={size} onChange={handleSizeChange}>
              <option value={10}>10개</option>
              <option value={20}>20개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
            </SizeSelect>
          </PageSizeSelector>
        </PageInfo>
      </PaginationContainer>
    </PaginationRoot>
  );
};

const PaginationRoot = styled.div`
  width: 100%;
  padding: 16px 20px;
  border-top: 1px solid rgba(112, 115, 124, 0.16);
  background: #fff;
  font-family: var(--font-family-base);

  @media (max-width: 768px) {
    padding: 12px 16px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const PageControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const NavButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(55, 56, 60, 0.61);

  &:hover:not(:disabled) {
    background-color: rgba(112, 115, 124, 0.05);
    color: #171719;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }

  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0 8px;

  @media (max-width: 768px) {
    margin: 0 4px;
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;
    justify-content: center;
    max-width: 100%;
  }
`;

const PageButton = styled.button<{ $active: boolean }>`
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: ${({ $active }) => ($active ? '#171719' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : 'rgba(46, 47, 51, 0.88)')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  letter-spacing: 0.01em;
  line-height: 146.7%;
  padding: 4px 8px;
  font-family: var(--font-family-base);

  &:hover {
    background-color: ${({ $active }) => ($active ? '#171719' : 'rgba(112, 115, 124, 0.05)')};
  }

  @media (max-width: 768px) {
    min-width: 28px;
    height: 28px;
    font-size: 14px;
    padding: 2px 6px;
  }

  @media (max-width: 480px) {
    min-width: 28px;
    height: 28px;
    font-size: 13px;
    padding: 2px 6px;
  }
`;

const PageInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: rgba(55, 56, 60, 0.61);

  @media (max-width: 1024px) {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    gap: 12px;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const PageStats = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 640px) {
    justify-content: center;
  }
`;

const StatsText = styled.span`
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 138.5%;

  @media (max-width: 480px) {
    text-align: center;
  }
`;

const DirectMove = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }

  @media (max-width: 640px) {
    justify-content: center;
  }
`;

const Label = styled.span`
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 138.5%;
`;

const PageInput = styled.input`
  width: 53px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  padding: 4px 6px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #171719;
  letter-spacing: 0.01em;
  line-height: 142.9%;
  font-family: var(--font-family-base);
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #171719;
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 28px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 24px;
    font-size: 12px;
  }
`;

const TotalPages = styled.span`
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 138.5%;
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    justify-content: center;
  }
`;

const SizeSelect = styled.select`
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  color: #171719;
  background: #fff;
  cursor: pointer;
  font-family: var(--font-family-base);
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #171719;
  }

  @media (max-width: 768px) {
    height: 28px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    height: 24px;
    font-size: 12px;
  }
`;

export default AdminPagination;
